import _ from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Routes, Route, useLocation, Navigate, useNavigate, Location, Outlet } from 'react-router-dom'
import { darkTheme, MeetingProvider, GlobalStyles } from 'amazon-chime-sdk-component-library-react'
import { ThemeProvider as ScThemeProvider } from 'styled-components'

import { createTheme, useTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import * as locales from '@mui/material/locale'

import ProtectedConsultationPage from '@/components/layout/ProtectedConsultationPage'
import ProtectedPage from '@/components/layout/ProtectedPage'
import ProtectedOfflinePage from '@/components/layout/ProtectedOfflinePage'

import { useGetEmergencyRoomsQuery } from '@/core/services/room'
import { Role } from '@/core/types/shared'

import DiagnosticLayout from '@/layouts/diagnostic/DiagnosticLayout'
import LandingLayout from '@/layouts/landing/LandingLayout'
import { MainLayout } from '@/layouts/main'
import OfflineLayout from '@/layouts/offline/OfflineLayout'

import AdminPage from '@/pages/admin/AdminPage'
import AgendaPage from '@/pages/agenda/AgendaPage'
import EstablishmentAgendaPage from '@/pages/agenda/EstablishmentAgendaPage'
import BookingPage from '@/pages/booking/BookingPage'
import { ConfigurationPage } from '@/pages/configuration'
import ConsultationPage from '@/pages/consultation/ConsultationPage'
import ConsultationsPage from '@/pages/consultations/ConsultationsPage'
import CreateConsultationPage from '@/pages/create-consultation/CreateConsultationPage'
import CreateExpertisePage from '@/pages/create-expertise/CreateExpertisePage'
import CreateMeetingLinkPage from '@/pages/meeting-link/CreateMeetingLinkPage'
import CreatePatientPage from '@/pages/create-patient/ui/CreatePatientPage'
import { EmergencyPage } from '@/pages/emergency'
import EmergenciesPage from '@/pages/emergencies/EmergenciesPage'
import { ExpertiseMeetingsPage } from '@/pages/expertise-meetings'
// import ExpertiseValidationPage from '@/pages/expertise/ExpertiseValidationPage'
import { ExpertiseValidationPage } from '@/pages/expertise-validation'
import ExpertiseUpdatePage from '@/pages/expertise/ExpertiseUpdatePage'
import ExpertisePage from '@/pages/expertise/ExpertisePage'
import ExpertisesPage from '@/pages/expertises/ExpertisesPage'
import FileUploadPage from '@/pages/file-upload/FileUploadPage'
import GettingStartedPage from '@/pages/getting-started/GettingStartedPage'
import HomePage from '@/pages/home/HomePage'
import IntegratedMeeting from '@/pages/meeting/IntegratedMeeting'
import { InfoPatientPage } from '@/pages/patient'
import LicensesPage from '@/pages/licenses/LicensesPage'
import LicenseMonitorPage from '@/pages/licenses/LicenseMonitorPage'
import { ConsultationLivePage } from '@/pages/consultation-live'
import { ConsultationLocalPage } from '@/pages/consultation-local'
import GroupConsultationPage from '@/pages/diagnostic/GroupConsultationPage'
import LocalConsultationsPage from '@/pages/localconsultations/LocalConsultationsPage'
import LoginPage from '@/pages/login/LoginPage'
import MedicalVideoPage from '@/pages/diagnostic/MedicalVideoPage'
import { ConsultationMeetingPage } from '@/pages/consultation-meeting'
import MeetingLinkPage from '@/pages/meeting-link/MeetingLinkPage'
import MeetingLinksPage from '@/pages/meeting-links/MeetingLinksPage'
import MobileConsultationPage from './pages/consultation/MobileConsultationPage'
import { MobileEmergencyPage } from '@/pages/emergency'
import NetsoinsPage from '@/pages/connector/netsoins/NetsoinsPage'
import NetworksPage from '@/pages/networks/NetworksPage'
import NotFoundPage from '@/pages/error/NotFoundPage'
import NotificationsPage from '@/pages/notifications/NotificationsPage'
import OnboardingPage from '@/pages/onboarding/OnboardingPage'
import OrderTrackingPage from '@/pages/order-tracking/OrderTracking'
import OrdersPage from '@/pages/orders/OrdersPage'
import { PatientsPage } from '@/pages/patients'
import PrescriptionPage from './pages/prescription/PrescriptionPage'
import ReserveConsultationDialog from '@/pages/waiting-rooms/containers/ReserveConsultationDialog'
import RocketChatPage from '@/pages/rocket-chat/RocketChatPage'
import StaffsPage from '@/pages/staffs/StaffsPage'
import { PatientsMergePage } from '@/pages/patients-merge'
import SubscribePage from '@/pages/subscribe/SubscribePage'
import SuperAdminPage from '@/pages/super-admin/SuperAdminPage'
import { CreateWaitingRoomPage, EditWaitingRoomPage, WaitingRoomPage } from '@/pages/waiting-room'
import WaitingRoomsPage from '@/pages/waiting-rooms/WaitingRoomsPage'
import TutorialsPage from '@/pages/tutorials/TutorialsPage'

import SnackbarProvider from '@/providers/SnackbarProvider'
import { ElaraGlobalProvider } from '@/providers/elara/ElaraGlobalProvider'
import { ElaraFAB } from '@/components/elara/ElaraFAB'
import { ElaraPanel } from '@/components/elara/ElaraPanel'
import { ElaraErrorBoundary } from '@/components/elara/ElaraErrorBoundary'
import { ElaraPanelProvider } from '@/providers/elara/ElaraPanelContext'
import { OfflineModeProvider } from '@/providers/OfflineModeProvider'
import { SyncProvider } from './core/offline/contexts/SyncContext'
import { AuthProvider } from '@/providers/AuthProvider'
import { ServiceWorkerInitializer } from '@/components/ServiceWorkerInitializer'

import { startup, useAppDispatch, useAppSelector, RootState } from '@/redux/rootReducer'
import { selectAppReady } from '@/redux/reducers/app.reducer'
import { selectAuthUser, selectIsAuthenticated, selectIsOfflineMode } from '@/redux/reducers/auth.reducer'
import { normalizeLanguage } from '@/i18n'

import { isStaff } from '@/utils/type-guards'

import 'intro.js/introjs.css'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import FormTemplatesPage from './pages/admin/forms/FormTemplatesPage'
import FormSubmissionPage from './pages/forms/FormSubmissionPage'
import ViewFormSubmissionPage from './pages/forms/ViewFormSubmissionPage'
import VisioConferencePage from './pages/conference/VisioConferencePage'
import ViewFormTemplatePage from './pages/admin/forms/ViewFormTemplatePage'
import ReportsPage from './pages/reports/ReportsPage'
import ShiftMarketplacePage from './pages/shift-marketplace/ShiftMarketplacePage'
import { TimeTrackingPage } from './pages/time-tracking'
import { MedConnectAgentChatPage, MedConnectAdminAgentPage } from './pages/medconnect-agent'
import AccessDenied from './components/ui/template/AccessDenied'
import ChatPage from './pages/chat/ui/ChatPage'
import DMPConnectProvider from './providers/DMPConnectProvider'

import { OfflineConsultationPage } from '@/pages/offline/consultation'
import { OfflineConsultationsPage } from '@/pages/offline/consultations'
import { OfflineCreatePatientPage } from '@/pages/offline/create-patient'
import { OfflineLocalConsultationPage } from '@/pages/offline/local-consultation'
import { OfflineLoginPage } from '@/pages/offline/login'
import { OfflineInfoPatientPage } from '@/pages/offline/patient'
import { OfflinePatientsPage } from '@/pages/offline/patients'
import { OfflineSyncPage } from '@/pages/offline/sync'
import { ExpertiseMeetingPage } from './pages/expertise-meeting'
import { EfficienceWebPage } from './pages/efficience-web'

type SupportedLocales = keyof typeof locales

function PageWrapper(props: { children: React.ReactElement }) {
  const { children } = props
  const location = useLocation()
  const authUser = useAppSelector(selectAuthUser)

  const from = _.get(location.state, 'from')

  if (!authUser) return null

  // comment getting started when user is logged in for now because Ephrem doesn't like it
  // if (authUser.status === 'MEDECIN' && authUser.isFirstConnection && location.pathname !== '/getting-started' && location.pathname !== from) {
  //   return <Navigate to="/getting-started" state={{ from: location.pathname }} />
  // }

  const adminRoutesDenied = !authUser.roles.includes(Role.Admin) && location.pathname.startsWith('/admin')
  const superAdminRoutesDenied = !authUser.roles.includes(Role.SuperAdmin) && location.pathname.startsWith('/super-admin')

  if (adminRoutesDenied || superAdminRoutesDenied) {
    return <AccessDenied />
  }

  return children
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const location = useLocation()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isOfflineMode = useAppSelector(selectIsOfflineMode)

  // If in offline mode and not already on an offline page, redirect.
  if (isOfflineMode && !location.pathname.startsWith('/offline')) {
    return <Navigate to="/offline/patients" replace />
  }

  // If not authenticated (and not in offline mode), redirect to the appropriate login page.
  if (!isAuthenticated && !isOfflineMode) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return children
}

function StartPage() {
  const authUser = useAppSelector(selectAuthUser)
  if (!authUser) return <AccessDenied />
  if (authUser.roles.includes(Role.SuperAdmin)) return <Navigate to="/super-admin" />
  if (authUser.roles.includes(Role.Admin)) return <Navigate to="/admin/staffs" />
  return <HomePage />
}

function App() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const isTooSmall = useIsMobile()

  const authUser = useAppSelector(selectAuthUser)
  const isAppReady = useAppSelector(selectAppReady)

  useGetEmergencyRoomsQuery(undefined, { skip: !authUser || (isStaff(authUser) && authUser.status !== 'MEDECIN') })

  const background: Location | undefined = _.get(location.state, 'background')
  // MUI has no en-SA locale bundle; without this alias the lookup below would silently fall back to frFR.
  const muiLanguage = i18n.language === 'en-SA' ? 'en-US' : i18n.language
  const locale = _.find(Object.keys(locales) as SupportedLocales[], (key) => !!_.startsWith(key, muiLanguage.replace('-', ''))) || 'frFR'

  const theme = useTheme()

  // Arabic locales (ar, ar-SA) read right-to-left: mirror the MUI theme and the
  // document so component and text direction follow the language.
  const direction: 'ltr' | 'rtl' = i18n.language.split('-')[0] === 'ar' ? 'rtl' : 'ltr'

  const themeWithLocale = useMemo(() => createTheme({ ...theme, direction }, locales[locale]), [direction, locale, theme])

  useEffect(() => {
    document.documentElement.lang = i18n.language
    document.documentElement.dir = direction
  }, [i18n, i18n.language, direction])

  useEffect(() => {
    if (!authUser?.userInfo.language) return

    const language = normalizeLanguage(authUser.userInfo.language)
    void i18n.changeLanguage(language)
    document.documentElement.lang = language
  }, [authUser, i18n])

  // startup
  // Important note: do not add any redux action other than startup !!!
  useEffect(() => {
    dispatch(startup())
  }, [dispatch])

  if (!isAppReady) return null

  return (
    <MuiThemeProvider theme={themeWithLocale}>
      <SnackbarProvider maxSnack={3}>
        <AuthProvider>
          <ServiceWorkerInitializer />
          <ElaraPanelProvider>
          <ElaraGlobalProvider>
          <ElaraErrorBoundary>
            <ElaraPanel />
          </ElaraErrorBoundary>
          <OfflineModeProvider>
            <SyncProvider>
              <DMPConnectProvider>
                {background && (
                  <Routes>
                    {/** route for dialog/modal */}
                    <Route
                      path="/expertises/create"
                      element={
                        <RequireAuth>
                          <PageWrapper>
                            <CreateExpertisePage />
                          </PageWrapper>
                        </RequireAuth>
                      }
                    />
                    <Route path="/waiting-rooms/:waitingRoomId/reserve" element={<ReserveConsultationDialog />} />
                  </Routes>
                )}
                <Routes location={background || location}>
                  <Route path="/forbidden" element={<div>forbidden</div>} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/offline-login" element={<OfflineLoginPage />} />
                  <Route
                    path="/meeting"
                    element={
                      <ScThemeProvider theme={darkTheme}>
                        <GlobalStyles />
                        <MeetingProvider>
                          <IntegratedMeeting />
                        </MeetingProvider>
                      </ScThemeProvider>
                    }
                  />
                  <Route element={<LandingLayout />}>
                    <Route path="/booking/:meetingLinkName" element={<BookingPage />} />
                    <Route path="/order-tracking" element={<OrderTrackingPage />} />
                  </Route>
                  <Route
                    element={
                      <RequireAuth>
                        <PageWrapper>
                          <MainLayout />
                        </PageWrapper>
                      </RequireAuth>
                    }
                  >
                    <Route path="/" element={<StartPage />} />
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedPage accessible={authUser?.roles?.includes(Role.Admin) ?? false}>
                          <AdminPage />
                        </ProtectedPage>
                      }
                    />
                    <Route path="/shift-marketplace" element={<ShiftMarketplacePage />} />
                    <Route path="/onboarding" element={<OnboardingPage />} />
                    <Route path="/agenda" element={<AgendaPage />} />
                    <Route path="/establishment-agenda" element={<EstablishmentAgendaPage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/configuration" element={<ConfigurationPage />} />
                    <Route path="/connector/netsoins/:consultationId" element={<NetsoinsPage />} />
                    <Route path="/consultations" element={<ConsultationsPage />} />
                    <Route path="/consultations/local" element={<LocalConsultationsPage />} />
                    <Route path="/forms/submit/:templateId/:patientId" element={<FormSubmissionPage />} />
                    <Route path="/forms/submit/:templateId/:patientId/:submissionId" element={<FormSubmissionPage />} />
                    <Route path="/forms/submissions/:submissionId" element={<ViewFormSubmissionPage />} />
                    <Route path="/forms-templates" element={<FormTemplatesPage />} />
                    <Route path="/consultations/create" element={<CreateConsultationPage onComplete={() => navigate('/consultations')} />} />
                    <Route
                      path="/consultations/:consultationId"
                      element={<ProtectedConsultationPage>{isTooSmall ? <MobileConsultationPage /> : <ConsultationPage />}</ProtectedConsultationPage>}
                    />
                    <Route path="/prescription" element={<PrescriptionPage />} />
                    <Route path="/emergencies" element={<EmergenciesPage />} />
                    <Route path="/expertise-meetings" element={<ExpertiseMeetingsPage />} />
                    <Route path="/expertises/create" element={<Navigate to="/expertises/create" state={{ background: { pathname: '/expertises' } }} />} />
                    <Route path="/expertises" element={<ExpertisesPage />} />
                    <Route path="/expertises/:expertiseId" element={<ExpertisePage />} />
                    <Route path="/expertises/:expertiseId/update" element={<ExpertiseUpdatePage />} />
                    <Route path="/expertises/:expertiseId/validation" element={<ExpertiseValidationPage />} />
                    <Route path="/licenses" element={<LicensesPage />} />
                    <Route path="/licenses/:licenseId" element={<LicenseMonitorPage />} />
                    <Route path="/meeting-links" element={<MeetingLinksPage />} />
                    <Route path="/meeting-links/:meetingLinkId" element={<MeetingLinkPage />} />
                    <Route path="/meeting-links/create" element={<CreateMeetingLinkPage />} />
                    <Route path="/meeting-links/orders" element={<OrdersPage />} />
                    <Route path="/networks/*" element={<NetworksPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/patients" element={<PatientsPage />} />
                    <Route path="/patients/create" element={<CreatePatientPage />} />
                    <Route path="/patients/merge" element={<PatientsMergePage />} />
                    <Route path="/patients/:patientId" element={<InfoPatientPage />} />
                    <Route path="/rocket-chat" element={<RocketChatPage />} />
                    <Route path="/staffs" element={<StaffsPage />} />
                    <Route path="/waiting-rooms/create" element={<ProtectedPage accessible={isStaff(authUser) && authUser?.status === 'MEDECIN'}><CreateWaitingRoomPage /></ProtectedPage>} />
                    <Route path="/waiting-rooms/:waitingRoomId" element={<ProtectedPage accessible={isStaff(authUser) && authUser?.status === 'MEDECIN'}><WaitingRoomPage /></ProtectedPage>} />
                    {/* Edit is creator-only (enforced by EditWaitingRoomPage + WaitingRoomCreatorGuard), so any staff
                        status may reach it — a PDS network admin can edit rooms they created. The doctor view above
                        stays MEDECIN-only: that is where patients get claimed. */}
                    <Route path="/waiting-rooms/:waitingRoomId/edit" element={<ProtectedPage accessible={isStaff(authUser)}><EditWaitingRoomPage /></ProtectedPage>} />
                    <Route path="/waiting-rooms" element={<WaitingRoomsPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/tutorials" element={<TutorialsPage />} />
                    <Route path="/tutorials/:id" element={<TutorialsPage />} />
                    <Route path="/time-tracking" element={<TimeTrackingPage />} />
                    <Route path="/medconnect-agent" element={<MedConnectAgentChatPage />} />
                    <Route path="/medconnect-agent/:sessionId" element={<MedConnectAgentChatPage />} />
                    <Route path="/medconnect-admin-agent" element={<MedConnectAdminAgentPage />} />
                    <Route path="/medconnect-admin-agent/:sessionId" element={<MedConnectAdminAgentPage />} />
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedPage accessible={authUser?.roles?.includes(Role.Admin) ?? false}>
                          <AdminPage />
                        </ProtectedPage>
                      }
                    />
                    <Route
                      path="/super-admin/*"
                      element={
                        <ProtectedPage accessible={authUser?.roles?.includes(Role.SuperAdmin) ?? false}>
                          <SuperAdminPage />
                        </ProtectedPage>
                      }
                    />
                  </Route>

                  {/* Offline Routes with Dedicated Layout */}
                  <Route
                    element={
                      <RequireAuth>
                        <PageWrapper>
                          <OfflineLayout />
                        </PageWrapper>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/offline/patients"
                      element={
                        <ProtectedOfflinePage>
                          <OfflinePatientsPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route
                      path="/offline/patients/create"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineCreatePatientPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route
                      path="/offline/patients/:patientId"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineInfoPatientPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route
                      path="/offline/consultations"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineConsultationsPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route
                      path="/offline/consultations/:consultationId"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineConsultationPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route
                      path="/offline/sync"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineSyncPage />
                        </ProtectedOfflinePage>
                      }
                    />
                  </Route>

                  <Route
                    element={
                      <RequireAuth>
                        <PageWrapper>
                          <DiagnosticLayout />
                        </PageWrapper>
                      </RequireAuth>
                    }
                  >
                    <Route
                      path="/consultations/:consultationId/group-live"
                      element={
                        <ProtectedConsultationPage>
                          <GroupConsultationPage />
                        </ProtectedConsultationPage>
                      }
                    />
                    <Route
                      path="/consultations/:consultationId/live"
                      element={
                        <ProtectedConsultationPage>
                          <ConsultationLivePage />
                        </ProtectedConsultationPage>
                      }
                    />
                    <Route
                      path="/consultations/:consultationId/local"
                      element={
                        <ProtectedConsultationPage>
                          <ConsultationLocalPage />
                        </ProtectedConsultationPage>
                      }
                    />
                    <Route
                      path="/consultations/:consultationId/meeting"
                      element={
                        <ProtectedConsultationPage>
                          <ConsultationMeetingPage />
                        </ProtectedConsultationPage>
                      }
                    />
                    <Route
                      path="/offline/consultations/:consultationId/local"
                      element={
                        <ProtectedOfflinePage>
                          <OfflineLocalConsultationPage />
                        </ProtectedOfflinePage>
                      }
                    />
                    <Route path="/expertise-meetings/:expertiseMeetingId" element={<ExpertiseMeetingPage />} />
                    <Route path="/emergency" element={isTooSmall ? <MobileEmergencyPage /> : <EmergencyPage />} />
                    <Route path="/emergencies/:roomId" element={isTooSmall ? <MobileEmergencyPage /> : <EmergencyPage />} />
                  </Route>
                  <Route
                    element={
                      <RequireAuth>
                        <Outlet />
                      </RequireAuth>
                    }
                  >
                    <Route path="/account-verification" element={<GettingStartedPage />} />
                    <Route path="/efficience-web" element={<EfficienceWebPage />} />
                    <Route path="/getting-started" element={<GettingStartedPage />} />
                    <Route path="/visioconference" element={<VisioConferencePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                  <Route path="/file-upload" element={<FileUploadPage />} />
                  <Route path="/subscribe" element={<SubscribePage />} />
                  <Route path="/m">
                    <Route path="medical-video" element={<MedicalVideoPage />} />
                  </Route>
                </Routes>
              </DMPConnectProvider>
            </SyncProvider>
          </OfflineModeProvider>
          </ElaraGlobalProvider>
          </ElaraPanelProvider>
        </AuthProvider>
      </SnackbarProvider>
    </MuiThemeProvider>
  )
}

export default App
